// /src/components/EmailVerification.js

import React, { useState } from 'react';
import { useNavigate, Link, useLocation } from 'react-router-dom';
import { FaEnvelope, FaSpinner, FaRedo, FaArrowLeft } from 'react-icons/fa';
import api from '../api/axios';
import { Card, CardHeader, CardTitle, CardContent } from './ui/card';
import { useTranslation } from 'react-i18next';

const EmailVerification = ({ email: propEmail, onVerificationSuccess }) => {
  const [verificationCode, setVerificationCode] = useState('');
  const [isVerifying, setIsVerifying] = useState(false);
  const [error, setError] = useState('');
  const [isResending, setIsResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const email = location.state?.email || propEmail;
  const { t } = useTranslation();

  const handleVerify = async (e) => {
    e.preventDefault();
    setIsVerifying(true);
    setError('');
  
    try {
      const response = await api.post('/auth/verify-email', {
        email,
        code: verificationCode
      });
  
      localStorage.setItem('token', response.data.token);
      localStorage.setItem('role', response.data.role);
      localStorage.setItem('userId', response.data.userId);
      // Defensive: caller may forget to pass the callback. Don't let that
      // block navigation — the token is already stored.
      try { onVerificationSuccess?.(); } catch (e) { console.error('onVerificationSuccess failed:', e); }
      navigate('/dashboard');
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to verify email');
    } finally {
      setIsVerifying(false);
    }
  };

  const handleResendCode = async () => {
    setIsResending(true);
    setError('');
    setResendSuccess(false);

    try {
      await api.post('/auth/resend-verification', { email });
      setResendSuccess(true);
      setTimeout(() => setResendSuccess(false), 5000);
    } catch (error) {
      setError(error.response?.data?.error || 'Failed to resend verification code');
    } finally {
      setIsResending(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-gray-900 px-4">
      {/* Brand Section */}
      <div className="text-center mb-8">
        <Link to="/">
          <h1 className="text-4xl font-bold text-white mb-2 hover:text-gray-200 transition-colors">
            {t('auth.common.brandName')}
          </h1>
        </Link>
      </div>
  
      <Card className="w-full max-w-md bg-gray-800 border-gray-700">
        <CardHeader className="relative pt-14 pb-6">
            {/* Back to Login Button */}
            <button
                onClick={() => navigate('/login')}
                className="absolute left-6 top-6 text-gray-400 hover:text-white transition-colors 
                        flex items-center gap-2 text-sm"
            >
                <FaArrowLeft />
                {t('auth.common.backToLogin')}
            </button>
            
            <CardTitle className="text-xl font-semibold text-center text-white">
                {t('auth.verification.title')}
            </CardTitle>
        </CardHeader>
  
        <CardContent>
          <div className="text-center mb-6">
            <div className="w-16 h-16 bg-indigo-600/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <FaEnvelope className="h-8 w-8 text-indigo-400" />
            </div>
            <p className="text-gray-300">
              {t('auth.verification.sentMessage')}
            </p>
            <p className="text-white font-medium">{email}</p>
          </div>
  
          <form onSubmit={handleVerify} className="space-y-6">
            <div>
              <label htmlFor="code" className="block text-sm font-medium text-gray-300 mb-1">
                {t('auth.verification.codeLabel')}
              </label>
              <input
                type="text"
                id="code"
                maxLength="6"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/\D/g, ''))}
                className="w-full px-3 py-2 bg-gray-700 border border-gray-600 rounded-lg 
                         text-white placeholder-gray-400 focus:border-indigo-500 
                         focus:ring-1 focus:ring-indigo-500 text-center text-2xl tracking-wide"
                placeholder="000000"
                required
              />
            </div>
  
            {error && (
              <div className="p-3 bg-red-500/10 border border-red-500/50 rounded-lg">
                <p className="text-sm text-red-500">{error}</p>
              </div>
            )}
  
            {resendSuccess && (
              <div className="p-3 bg-green-500/10 border border-green-500/50 rounded-lg">
                <p className="text-sm text-green-500">{t('auth.verification.resendSuccess')}</p>
              </div>
            )}
  
            <div className="flex flex-col gap-4">
              <button
                type="submit"
                disabled={isVerifying || verificationCode.length !== 6}
                className="w-full flex justify-center items-center px-4 py-2 border border-transparent 
                         rounded-lg shadow-sm text-sm font-medium text-white bg-indigo-600 
                         hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 
                         focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isVerifying ? (
                  <>
                    <FaSpinner className="animate-spin mr-2" />
                    {t('auth.verification.verifying')}
                  </>
                ) : (
                  t('auth.verification.verifyButton')
                )}
              </button>
  
              <button
                type="button"
                onClick={handleResendCode}
                disabled={isResending}
                className="text-indigo-400 hover:text-indigo-300 text-sm flex items-center 
                         justify-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isResending ? (
                  <>
                    <FaSpinner className="animate-spin" />
                    {t('auth.verification.resending')}
                  </>
                ) : (
                  <>
                    <FaRedo />
                    {t('auth.verification.resendButton')}
                  </>
                )}
              </button>
            </div>
          </form>
  
          <p className="mt-4 text-sm text-gray-400 text-center">
            {t('auth.verification.spamNote')}
          </p>
        </CardContent>
      </Card>
    </div>
  );
};

export default EmailVerification;